/**
 * Captura de origem de tráfego (UTM / click id / referrer) e ponte pro
 * `marketingData` do orderForm da VTEX -- é o que faz `utm_*` da URL de
 * entrada chegar ao pedido (e dali ao CRM, catalogo.pedidos.utm_*). Sem isto
 * o storefront headless nunca grava UTM: o app da VTEX que faria isso numa
 * loja padrão não roda aqui (ver useAttributionSync.ts).
 *
 * `parseTouch` é pura (recebe search/referrer/host) pra ser testável sem DOM.
 * Last-touch: um toque novo e não-direto substitui o anterior; visita direta
 * (sem UTM, click id nem referrer externo) nunca apaga a origem guardada.
 */
import { getCookie, setCookie } from "~/utils/cookies";
import { hasCookieConsent } from "~/utils/consent";
import { getOrCreateVisitorId } from "~/utils/visitorId";

export const TOUCH_COOKIE = "dt_touch";
const TOUCH_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 dias

// Só estes 3 campos existem em marketingData da VTEX com semântica de UTM
// (utmiX são internos e já usados pela consultora, ver VendorCoupon.tsx).
export interface Touch {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
}

const MAX_LEN = 100;

function limpar(valor: string | null | undefined): string {
  return (valor ?? "").trim().slice(0, MAX_LEN);
}

// Hosts próprios: navegação interna ou volta do checkout não é toque novo.
const HOSTS_PROPRIOS = ["danielatombini.com.br", "tombini.ind.br"];

function ehHostProprio(host: string, hostAtual: string): boolean {
  if (host === hostAtual) return true;
  return HOSTS_PROPRIOS.some((p) => host === p || host.endsWith(`.${p}`));
}

/** Host do referrer quando é EXTERNO (não próprio, URL válida); senão null. */
export function externalReferrerHost(referrer: string, hostAtual: string): string | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    if (!host || ehHostProprio(host, hostAtual.toLowerCase())) return null;
    return host;
  } catch {
    return null;
  }
}

function classificarReferrer(host: string): { source: string; medium: string } {
  if (host === "instagram.com" || host.endsWith(".instagram.com")) {
    return { source: "instagram", medium: "social" };
  }
  if (host === "facebook.com" || host.endsWith(".facebook.com") || host === "fb.com") {
    return { source: "facebook", medium: "social" };
  }
  if (/(^|\.)google\.[a-z.]+$/.test(host)) return { source: "google", medium: "organic" };
  if (host === "bing.com" || host.endsWith(".bing.com")) return { source: "bing", medium: "organic" };
  return { source: host.replace(/^www\./, ""), medium: "referral" };
}

/**
 * Prioridade: utm_* explícito > click id (gclid/fbclid) > referrer externo.
 * Devolve null quando nada indica origem (visita direta ou navegação interna).
 */
export function parseTouch(search: string, referrer: string, hostAtual: string): Touch | null {
  const params = new URLSearchParams(search);

  const utmSource = limpar(params.get("utm_source")).toLowerCase();
  if (utmSource) {
    return {
      utmSource,
      utmMedium: limpar(params.get("utm_medium")).toLowerCase(),
      utmCampaign: limpar(params.get("utm_campaign")),
    };
  }
  if (params.get("gclid")) return { utmSource: "google", utmMedium: "cpc", utmCampaign: "" };
  if (params.get("fbclid")) return { utmSource: "meta", utmMedium: "paid_social", utmCampaign: "" };

  const host = externalReferrerHost(referrer, hostAtual);
  if (host) {
    const { source, medium } = classificarReferrer(host);
    return { utmSource: source, utmMedium: medium, utmCampaign: "" };
  }
  return null;
}

/**
 * Toque completo pro CRM (catalogo.touchpoints). Diferente de `Touch`
 * (só os 3 campos que cabem no marketingData da VTEX), leva também
 * utm_content/term (nome do anúncio), o TIPO do click id (nunca o valor), o
 * path de entrada sem querystring e o host do referrer externo. A
 * classificação em canal é feita no CRM, não aqui.
 */
export interface TouchpointPayload {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  click_id_tipo: "gclid" | "fbclid" | "";
  landing_path: string;
  referrer_host: string;
}

export function buildTouchpoint(
  search: string,
  referrer: string,
  pathname: string,
  hostAtual: string,
): TouchpointPayload | null {
  const touch = parseTouch(search, referrer, hostAtual);
  if (!touch) return null;
  const params = new URLSearchParams(search);
  return {
    utm_source: touch.utmSource,
    utm_medium: touch.utmMedium,
    utm_campaign: touch.utmCampaign,
    utm_content: limpar(params.get("utm_content")),
    utm_term: limpar(params.get("utm_term")),
    click_id_tipo: params.get("gclid") ? "gclid" : params.get("fbclid") ? "fbclid" : "",
    landing_path: limpar(pathname.split("?")[0]),
    referrer_host: externalReferrerHost(referrer, hostAtual) ?? "",
  };
}

const TEXTO_MAX = 200;
const CAMPOS_TEXTO = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "landing_path", "referrer_host",
] as const;

/**
 * Sanitiza o que chega na rota pública /tracking/touchpoints (qualquer um
 * pode POSTar): só chaves conhecidas, strings capadas, click_id_tipo em
 * enum. Devolve null se não for objeto. O CRM valida de novo.
 */
export function sanitizeTouchpoint(raw: unknown): TouchpointPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const out = {} as Record<string, string>;
  for (const campo of CAMPOS_TEXTO) {
    const v = o[campo];
    out[campo] = typeof v === "string" ? v.trim().slice(0, TEXTO_MAX) : "";
  }
  out.click_id_tipo = o.click_id_tipo === "gclid" || o.click_id_tipo === "fbclid" ? o.click_id_tipo : "";
  return out as unknown as TouchpointPayload;
}

export function serializeTouch(touch: Touch): string {
  return JSON.stringify(touch);
}

export function deserializeTouch(raw: string | null): Touch | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as Partial<Touch>;
    if (typeof o.utmSource !== "string" || !o.utmSource) return null;
    return {
      utmSource: limpar(o.utmSource),
      utmMedium: limpar(o.utmMedium),
      utmCampaign: limpar(o.utmCampaign),
    };
  } catch {
    return null;
  }
}

export function sameTouch(a: Touch, b: { utmSource?: unknown; utmMedium?: unknown; utmCampaign?: unknown }): boolean {
  return (
    a.utmSource === (b.utmSource ?? "") &&
    a.utmMedium === (b.utmMedium ?? "") &&
    a.utmCampaign === (b.utmCampaign ?? "")
  );
}

export function getStoredTouch(): Touch | null {
  try {
    return deserializeTouch(getCookie(TOUCH_COOKIE));
  } catch {
    return null;
  }
}

/**
 * Lê a URL/referrer atuais; se houver toque novo, grava no cookie e o
 * devolve com `fresh: true`. Sem toque novo devolve o já guardado
 * (`fresh: false`). Nunca lança, nunca roda sem consentimento.
 */
export interface CapturedTouch {
  touch: Touch | null;
  fresh: boolean;
  /** Só quando `fresh`: o toque completo a enviar ao CRM. */
  touchpoint: TouchpointPayload | null;
}

export function captureTouch(): CapturedTouch {
  const vazio: CapturedTouch = { touch: null, fresh: false, touchpoint: null };
  try {
    if (typeof window === "undefined" || !hasCookieConsent()) return vazio;
    const { search, pathname, hostname } = window.location;
    const novo = parseTouch(search, document.referrer, hostname);
    if (novo) {
      setCookie(TOUCH_COOKIE, serializeTouch(novo), TOUCH_MAX_AGE_SECONDS);
      return {
        touch: novo,
        fresh: true,
        touchpoint: buildTouchpoint(search, document.referrer, pathname, hostname),
      };
    }
    return { ...vazio, touch: getStoredTouch() };
  } catch {
    return vazio;
  }
}

const SENT_KEY = "dt_touchpoint_sent";

/**
 * Manda o toque ao proxy server-side (/tracking/touchpoints), que lê o
 * `dt_visitor_id` do cookie e repassa ao CRM com o token. Best-effort:
 * nunca lança. Trava por sessão (sessionStorage) pra um reload da mesma
 * entrada não virar toque duplicado -- o CRM ainda deduplica por 30 min.
 */
export function sendTouchpoint(tp: TouchpointPayload): void {
  try {
    if (typeof window === "undefined" || !hasCookieConsent()) return;
    const chave = JSON.stringify(tp);
    try {
      if (sessionStorage.getItem(SENT_KEY) === chave) return;
      sessionStorage.setItem(SENT_KEY, chave);
    } catch {
      // sessionStorage bloqueado: segue sem a trava (o CRM deduplica)
    }
    // Garante o cookie dt_visitor_id ANTES do envio -- o proxy depende dele.
    if (!getOrCreateVisitorId()) return;
    const body = JSON.stringify({ touchpoint: tp });
    const enviado = navigator.sendBeacon?.(
      "/tracking/touchpoints",
      new Blob([body], { type: "application/json" }),
    );
    if (!enviado) {
      void fetch("/tracking/touchpoints", {
        method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // tracking nunca quebra a loja
  }
}
